
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {AzimuthAuxiliaryVisual} from "../MeasureAuxiliaryVisuals.js";
import {createFeature} from "../Feature.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {createMarker, createDashedLine, setLinePositions} from "./perpendicularCapture.js";
import {captureFixedPoints} from "./pipeTool.js";
import {gridAzimuth, horizontalDistance} from "../geometry/measurementGeometry.js";
import {formatLength} from "./distanceTool.js";
import {CURSOR_MARKER_COLOR} from "../CursorPointMarker.js";

import {t, T} from "../i18n/index.js";
const PREVIEW_COLOR = 0xffdc00; // samme konstruktions-forhåndsvisning som resten af kodebasen

// Mål azimut: kopieret fra det gamle native "Azimuth"-måleværktøj og genskrevet
// til Digitaliserings Målinger-format. To punkter: azimut er retningen fra
// punkt 1 til punkt 2, i grader med uret fra NETS nord (UTM-nord, +Y), 2
// decimaler. BEVIDST afvigelse fra det gamle værktøj: dets formel regnede
// direkte på længde-/breddegrad-forskelle uden cos(bredde) (ca. 47 i stedet
// for 30 grader ved Aarhus) og brugte kun første punktskys projektion - se
// geometry/measurementGeometry.js's gridAzimuth(). Her beregnes retningen i
// stedet direkte i koordinatsystemet, uafhængigt af projektion/model.
export const AzimuthTool = {
	id: "digitize_measure_azimuth",
	name: T("Mål azimut"),
	category: "measurement",
	kind: "measurement",
	toolbarGroup: "measurement",
	toolbarOrder: 80,
	icon: "measure_azimuth.svg",
	resultGeometryType: "LineString",
	fixedVertexCount: 2,
	tooltip: T("Mål retningen fra det første til det andet punkt som azimut (grader med uret fra nettets nord)."),
	instructions: T("Venstreklik for at vælge udgangspunktet, derefter målpunktet."),

	customCapture(dm, tool){
		runAzimuthCapture(dm, tool);
	},
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished measurement keeps its labels and azimuth arc.
	decorateLoaded(visual){ applyAzimuthRepresentation(visual); },
};

DigitizationTools.register(AzimuthTool);

const STEP_INSTRUCTIONS = [
	null,
	T("Venstreklik for at vælge udgangspunktet."),
	T("Venstreklik for at vælge målpunktet - azimut måles fra udgangspunktet."),
];

// To-punkts-pendant til shapeCaptureHelpers.js's captureThreePointsWithPreview()
// (samme mærker/stiplede forhåndsvisningslinje, ikke ændret/delt for ikke at
// røre Cirkel/Rektangel/Bue, som bruger den).
function captureTwoPointsWithPreview(dm, tool){
	const scene = dm.scene;
	const markers = [createMarker(CURSOR_MARKER_COLOR), createMarker(CURSOR_MARKER_COLOR)];
	const line = createDashedLine(PREVIEW_COLOR);
	for(const m of markers) scene.add(m);
	scene.add(line);

	const sizeMarker = (marker, worldPos) => {
		const camera = dm.viewer.scene.getActiveCamera();
		const size = dm.renderer.getSize(new THREE.Vector2());
		const cameraPos = camera.getWorldPosition(new THREE.Vector3());
		marker.scale.setScalar(computeScreenSpaceScale(camera, worldPos, cameraPos, size.width, size.height, ScreenSpaceSizes.computedPoint));
	};

	dm._showInstruction(tool.name, t(STEP_INSTRUCTIONS[1]));

	return captureFixedPoints(dm, tool, 2, (n) => (STEP_INSTRUCTIONS[n] ? t(STEP_INSTRUCTIONS[n]) : null), (point, count) => {
		const marker = markers[count - 1];
		marker.position.copy(point);
		marker.visible = true;
		sizeMarker(marker, point);

		if(count === 2){
			setLinePositions(line, markers[0].position, point);
			line.visible = true;
		}
	}).then((points) => {
		for(const m of markers){ scene.remove(m); m.geometry.dispose(); m.material.dispose(); }
		scene.remove(line);
		line.geometry.dispose();
		line.material.dispose();
		return points;
	});
}

function runAzimuthCapture(dm, tool){
	captureTwoPointsWithPreview(dm, tool).then((points) => {
		if(!points) return; // ESC/skift af værktøj
		finalizeAzimuth(dm, tool, points);

		if(dm.activeTool === tool) dm.startInsertion(tool);
	});
}

function summaryLines(raw, visual){
	if(raw.length < 2) return null;
	const azimuth = gridAzimuth(raw[0], raw[1]);
	if(azimuth === null) return null; // samme XY: retningen er udefineret
	return [
		visual.name,
		t("Azimut (nettets nord): {0}°", azimuth.toFixed(2)),
		t("Vandret afstand: {0}", formatLength(horizontalDistance(raw[0], raw[1]))),
	];
}

// Lidt forbi målpunktet i målingens egen retning, uden for azimut-buen/labelen.
function summaryAnchor(raw){
	return {
		x: raw[1][0] + (raw[1][0] - raw[0][0]) * 0.2,
		y: raw[1][1] + (raw[1][1] - raw[0][1]) * 0.2,
		z: raw[1][2] + (raw[1][2] - raw[0][2]) * 0.2,
	};
}

// The finished azimuth measurement: hidden vertex labels, a length label, the result label and the azimuth arc, kept in step with the
// geometry. Shared by the capture flow and the restore hook (decorateLoaded).
function applyAzimuthRepresentation(visual){
	visual.setVertexLabelsSuppressed(true);
	visual.enableSegmentLabels((a, b) => formatLength(a.distanceTo(b)));
	visual.enableSummaryLabel(summaryLines, summaryAnchor);

	const auxiliary = new AzimuthAuxiliaryVisual();
	visual.add(auxiliary);
	visual.userData.auxiliaryVisual = auxiliary;
	const refresh = () => {
		const [from, to] = visual.points;
		auxiliary.updateFromPoints(from && from.position, to && to.position);
	};
	refresh();
	visual.addEventListener("geometry_changed", refresh);
}

function finalizeAzimuth(dm, tool, points){
	const visual = new FeatureVisual("LineString");
	for(const p of points) visual.appendAtEnd(p);
	applyAzimuthRepresentation(visual);

	const feature = createFeature({
		geometryType: "LineString",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.featureType || tool.name,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
	});

	visual.setInfo(feature.properties.name, feature.properties.description);
	dm.scene.add(visual);
	dm.registerNewFeature(feature, visual);
}
